import { useEffect, useState } from 'react';
import { API_CALLS } from '../api';
import type { AssociatedProviderOption } from '../types';

type Props = {
  employerId: number | undefined;
  selectedProviderIds: number[];
  onChange: (providerIds: number[]) => void;
};

export const AssociatedProviderPicker = ({ employerId, selectedProviderIds, onChange }: Props) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<AssociatedProviderOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!employerId) {
      setOptions([]);
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      API_CALLS.getPickerProviders({ employerId, search })
        .then((rows) => {
          if (!active) return;
          setOptions(rows);
          setError(null);
        })
        .catch((err) => {
          if (!active) return;
          setOptions([]);
          setError(err instanceof Error ? err.message : 'Unable to load associated providers.');
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [employerId, search]);

  const toggleProvider = (providerId: number) => {
    if (selectedProviderIds.includes(providerId)) {
      onChange(selectedProviderIds.filter((id) => id !== providerId));
      return;
    }
    onChange([...selectedProviderIds, providerId]);
  };

  return (
    <div className="mt-4 rounded-md border border-grey-900 p-3">
      <h3 className="body-text-sb mb-2">Associated providers (optional)</h3>
      <input
        type="text"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="mb-3 w-full rounded-md border border-grey-800 px-3 py-2 text-sm outline-none focus:border-primary-t-50"
        placeholder="Search providers"
      />
      <div className="max-h-48 space-y-2 overflow-y-auto">
        {loading && <p className="text-sm text-grey-500">Loading providers...</p>}
        {!loading && error && <p className="text-sm text-error">{error}</p>}
        {!loading && !error && options.length === 0 && (
          <p className="text-sm text-grey-500">No associated providers found</p>
        )}
        {!loading &&
          options.map((option) => (
            <label
              key={option.providerId}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-grey-800 p-2 hover:border-primary-t-50"
            >
              <input
                type="checkbox"
                checked={selectedProviderIds.includes(option.providerId)}
                onChange={() => toggleProvider(option.providerId)}
                className="mt-1"
              />
              <span>
                <span className="block text-[1rem] font-semibold text-black">
                  {option.displayName}
                </span>
                <span className="block text-xs text-grey-500">
                  {[option.providerType, option.providerCategory].filter(Boolean).join(' / ') ||
                    'Provider'}
                </span>
              </span>
            </label>
          ))}
      </div>
    </div>
  );
};
